import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KEYS = [["height", "Altezza cliente"], ["bust", "Seno"], ["waist_narrow", "Vita stretta"], ["waist_button", "Vita abbottonatura"], ["hips", "Bacino glutei"]];
const defaultRows = () => [{}, {}, {}, {}];
const withRows = (stored, sizeList) => Object.fromEntries(sizeList.map((s) => {
  const r = stored[s];
  const rows = Array.isArray(r) ? r : r ? [r] : [];
  return [s, [...rows, ...defaultRows().slice(rows.length)].slice(0, Math.max(4, rows.length))];
}));

const Cell = ({ v, onChange, testid }) => (
  <Input type="number" step="0.5" value={v ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    className="h-8 w-16 px-1.5 text-xs" data-testid={testid} />
);

const NONE = [];

export const SizeChartDialog = ({ product, onClose }) => {
  const qc = useQueryClient();
  const { data: charts = NONE } = useQuery({ queryKey: ["size-charts"], queryFn: async () => (await api.get("/size-charts")).data, enabled: !!product });
  const [sizes, setSizes] = useState({});
  const model = (product?.name || "").trim().toLowerCase();
  const sizeList = (product?.sizes || []).map((x) => x.size).join("|");
  useEffect(() => { setSizes(withRows(charts.find((c) => c.model === model)?.sizes || {}, sizeList ? sizeList.split("|") : [])); }, [charts, model, sizeList]);
  if (!product) return null;
  const set = (s, i, k, b, v) => setSizes((x) => ({ ...x, [s]: x[s].map((row, j) => (j === i ? { ...row, [k]: { ...(row[k] || {}), [b]: v } } : row)) }));
  const save = async () => {
    try {
      await api.put("/size-charts", { name: product.name, sizes });
      toast.success("Tabella misure salvata per tutti i colori del modello");
      qc.invalidateQueries({ queryKey: ["size-charts"] });
      qc.invalidateQueries({ queryKey: ["activity-entity"] });
      onClose();
    } catch (e) { toast.error("Salvataggio non riuscito"); throw e; }
  };
  const others = charts.filter((c) => c.model !== model);
  const first = product.sizes[0]?.size;
  const copyBands = () => {
    const bands = (sizes[first] || []).map((r) => r.height);
    setSizes((x) => Object.fromEntries(Object.entries(x).map(([s, rows]) => [s, s === first ? rows
      : bands.map((h, i) => ({ ...(rows[i] || {}), height: { ...(h || {}) } }))])));
    toast.success(`Fasce di altezza della ${first} copiate su tutte le taglie`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-auto" data-testid="size-chart-dialog" onSave={save}>
        <DialogHeader><DialogTitle>Tabella misure · {product.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>Per ogni taglia 4 righe, una per fascia di altezza della cliente · intervalli da–a in cm · lascia vuote le misure che non contano · vale per tutti i colori</span>
          <div className="flex flex-wrap gap-2">
            {product.sizes.length > 1 && (
              <Button variant="outline" size="sm" className="h-8" onClick={copyBands} data-testid="size-chart-copy-bands">
                Copia fasce della {first} su tutte le taglie
              </Button>
            )}
            {others.length > 0 && (
            <Select onValueChange={(m) => setSizes(withRows(others.find((c) => c.model === m)?.sizes || {}, sizeList.split("|")))}>
              <SelectTrigger className="h-8 w-60" data-testid="size-chart-copy"><SelectValue placeholder="Copia da…" /></SelectTrigger>
              <SelectContent>{others.map((c) => <SelectItem key={c.model} value={c.model}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          </div>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-muted-foreground"><th className="py-2 text-left">Taglia</th>{KEYS.map(([k, l]) => <th key={k} className="px-1 text-left font-medium">{l}</th>)}</tr></thead>
          <tbody>
            {product.sizes.map(({ size: s }) => (sizes[s] || []).map((row, i) => (
              <tr key={`${s}-${i}`} className={i === 0 ? "border-t-2 border-border" : "border-t border-border/30"} data-testid={`size-chart-row-${s}-${i}`}>
                {i === 0 && <td rowSpan={sizes[s].length} className="py-2 align-top font-mono-erp text-sm font-semibold">{s}</td>}
                {KEYS.map(([k]) => (
                  <td key={k} className={`px-1 py-1.5 ${k === "height" ? "bg-primary/5" : ""}`}><div className="flex items-center gap-1">
                    <Cell v={row[k]?.min} onChange={(v) => set(s, i, k, "min", v)} testid={`sc-${s}-${i}-${k}-min`} />–
                    <Cell v={row[k]?.max} onChange={(v) => set(s, i, k, "max", v)} testid={`sc-${s}-${i}-${k}-max`} />
                  </div></td>
                ))}
              </tr>
            )))}
          </tbody>
        </table>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} data-testid="size-chart-save">Salva tabella</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
